/**
 * useUserProfile — signup-and-billing Phase 1 Session 6.
 *
 * Reads the full `user_profiles` row via `GET /api/admin/me/status` (same
 * endpoint `useApprovalStatus` uses; we accept the small double-fetch since
 * both hooks are cheap + cache for 60s). Provides:
 *
 *   - `useUserProfile()` — full profile read with derived `needsOnboarding`
 *   - `usePatchUserProfile()` — mutation that invalidates BOTH this hook's
 *     cache key AND the `useApprovalStatus` cache key
 *
 * Type-only import for `UserProfile` from `@arcanum-hq/supabase` is safe in
 * web (Vite handles ESM cleanly) — only the api package has the documented
 * ts-jest constraint.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { useAuth } from './use-auth';
import type { UserProfile, UserProfilePatch } from '@arcanum-hq/shared-types';

interface UserProfileEnvelope {
  data: UserProfile;
}

export function useUserProfile() {
  const { user } = useAuth();

  const query = useQuery<UserProfile>({
    queryKey: queryKeys.user.profile(),
    queryFn: async () => {
      const envelope = await api.get<UserProfileEnvelope>(
        '/api/admin/me/status'
      );
      return envelope.data;
    },
    enabled: !!user,
    staleTime: 60_000,
    // refetchOnWindowFocus removed 2026-05-27 — handled centrally by
    // <FocusResumeCoordinator> to avoid the idle-tab refetch thundering herd.
  });

  const profile = query.data ?? null;
  // An authenticated user needs onboarding when EITHER:
  //   (a) no user_profiles row exists yet — the invited-user state (admin
  //       `inviteUser` writes only auth.users; the profile row is created
  //       when the user completes the wizard via `usePatchUserProfile`); OR
  //   (b) the row exists but `onboarding_completed=false`.
  // Pre-fix the predicate was `!!profile && profile.onboarding_completed === false`
  // which fell through to the unauth wizard branch for invited users and
  // tried to `signUp` an already-existing auth.users row → "User already
  // registered" hard-fail. The `profileLoading && user → return null` guard
  // in routes/signup.tsx handles the transient pre-load state, so a true
  // post-load null genuinely means "no row yet".
  const needsOnboarding =
    !!user && (!profile || profile.onboarding_completed === false);

  return {
    profile,
    needsOnboarding,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

export function usePatchUserProfile() {
  const qc = useQueryClient();
  const { user } = useAuth();

  return useMutation<UserProfile, Error, UserProfilePatch>({
    mutationFn: async (patch) => {
      const envelope = await api.patch<UserProfileEnvelope>(
        '/api/users/me',
        patch
      );
      return envelope.data;
    },
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.user.profile(), data);
      // Bust the useApprovalStatus cache too — same underlying row.
      qc.invalidateQueries({
        queryKey: ['admin', 'me', 'status', user?.id ?? 'anon'],
      });
    },
  });
}
